/**
 * 服务端存储实现（基于 Map，用于 SSR）
 */

import type { IStorage, StorageOptions, StorageItem } from './Storage'

export class ServerStorage implements IStorage {
  private storage: Map<string, string>
  private prefix: string
  private ttl?: number

  constructor(options: StorageOptions = {}) {
    this.storage = new Map()
    this.prefix = options.prefix || 'csd_'
    this.ttl = options.ttl
  }

  private getKey(key: string): string {
    return `${this.prefix}${key}`
  }

  private isExpired(item: StorageItem): boolean {
    if (!item.ttl) return false
    return Date.now() - item.timestamp > item.ttl
  }

  getItem<T = any>(key: string): T | null {
    try {
      const fullKey = this.getKey(key)
      const raw = this.storage.get(fullKey)

      if (!raw) return null

      const item: StorageItem<T> = JSON.parse(raw)

      // 检查是否过期
      if (this.isExpired(item)) {
        this.removeItem(key)
        return null
      }

      return item.value
    } catch (error) {
      console.error(`[ServerStorage] Failed to get item: ${key}`, error)
      return null
    }
  }

  setItem<T = any>(key: string, value: T): void {
    try {
      const fullKey = this.getKey(key)
      const item: StorageItem<T> = {
        value,
        timestamp: Date.now(),
        ttl: this.ttl
      }

      this.storage.set(fullKey, JSON.stringify(item))
    } catch (error) {
      console.error(`[ServerStorage] Failed to set item: ${key}`, error)
    }
  }

  removeItem(key: string): void {
    try {
      const fullKey = this.getKey(key)
      this.storage.delete(fullKey)
    } catch (error) {
      console.error(`[ServerStorage] Failed to remove item: ${key}`, error)
    }
  }

  clear(): void {
    try {
      const keys = this.keys()
      keys.forEach((key) => this.removeItem(key))
    } catch (error) {
      console.error('[ServerStorage] Failed to clear storage', error)
    }
  }

  hasItem(key: string): boolean {
    return this.getItem(key) !== null
  }

  keys(): string[] {
    try {
      const allKeys: string[] = []
      for (const key of this.storage.keys()) {
        if (key.startsWith(this.prefix)) {
          allKeys.push(key.substring(this.prefix.length))
        }
      }
      return allKeys
    } catch (error) {
      console.error('[ServerStorage] Failed to get keys', error)
      return []
    }
  }
}
